/**
 * Chargebee hosted-checkout URL construction.
 *
 * Chargebee only persists custom fields that are passed with their
 * entity prefix — `customer[cf_foo]=bar`. Bare `cf_foo=bar` params are
 * silently ignored, as are plain `utm_source=…` params, which Chargebee
 * does not store anywhere. Everything we want to keep therefore has to
 * be mapped onto a Customer custom field that exists in the Chargebee
 * admin, with the casing matching the configured API name exactly.
 *
 * Fields consumed here (all Customer-level):
 *
 *   cf_utm_source        cf_gclid          created for marketing attribution
 *   cf_utm_medium        cf_fbclid
 *   cf_utm_campaign      cf_li_fat_id
 *   cf_utm_content       cf_msclkid
 *   cf_utm_term          cf_ttclid
 *   cf_landing_page      cf_click_ts
 *   cf_referring_website  cf_ga_client_id
 *   cf_fpr_ref            — which affiliate sent them
 *
 * `cf_tid` is deliberately NOT written here — see the note in
 * getCheckoutAttributionParams.
 *
 * A field that doesn't exist in Chargebee is dropped without error, so
 * this is safe to ship ahead of the admin changes — it just won't
 * capture anything until the fields are created.
 *
 * This module is intentionally duplicated in the marketing repo
 * (marketing-and-news-app). Keep them in sync.
 */

import { getAttribution } from "./attribution";
import { getClickIds, getClickIdTimestamp } from "./clickIds";
import { getFirstPromoterRef } from "./firstPromoter";
import { getGaClientId } from "./gaClientId";

export const CHARGEBEE_CHECKOUT_BASE =
  "https://newsquawk.chargebee.com/hosted_pages/checkout";

/**
 * Builds the `customer[cf_*]` query string carrying attribution and ad
 * click IDs. Returns "" when we know nothing about the visitor.
 *
 * Note the bracket syntax is left literal rather than percent-encoded —
 * that's the form Chargebee documents and parses. Values are encoded.
 */
export function getCheckoutAttributionParams(): string {
  const attr = getAttribution();
  const clickIds = getClickIds();

  const fields: Record<string, string | undefined | null> = {
    cf_utm_source: attr?.utm_source,
    cf_utm_medium: attr?.utm_medium,
    cf_utm_campaign: attr?.utm_campaign,
    cf_utm_content: attr?.utm_content,
    cf_utm_term: attr?.utm_term,
    cf_landing_page: attr?.landing_page,
    cf_referring_website: attr?.referrer_domain,
    cf_gclid: clickIds.gclid,
    cf_fbclid: clickIds.fbclid,
    cf_li_fat_id: clickIds.li_fat_id,
    cf_msclkid: clickIds.msclkid,
    cf_ttclid: clickIds.ttclid,
    // Meta needs the click time to build a usable `fbc`, and no platform
    // can recover it after the fact. Absent for visitors whose click IDs
    // were captured before this field existed.
    cf_click_ts: getClickIdTimestamp(),
    // GA4's client ID, so the conversion pref-storage reports later lands on
    // this visitor rather than a synthetic new one. See gaClientId.ts for why
    // this and not FPID.
    cf_ga_client_id: getGaClientId(),
    // Which promoter sent them. Our own field, our own namespace — see the
    // cf_tid note below for why this isn't written there.
    cf_fpr_ref: getFirstPromoterRef(),
    // NOT writing cf_tid. Live customer records show it holding a UUID
    // (e.g. c7601f5a-80b0-4774-8210-cd3d385867e7) alongside a
    // cf_referring_website containing a raw document.referrer — neither
    // shape is produced by this codebase, so another system owns that
    // field. A FirstPromoter promoter ref is a short slug ("joe13"), so
    // writing one here would both fail to match and clobber whatever
    // populates it today.
    //
    // The promoter identity goes to cf_fpr_ref above instead, which is
    // ours and serves our own reporting. FirstPromoter's own attribution
    // needs a tid it minted; supplying that means either running fpr.js
    // (rejected) or POSTing ref_id to /api/v2/track/signup server-side.
    // See docs/sgtm-migration-audit.md.
  };

  const parts: string[] = [];
  for (const [key, value] of Object.entries(fields)) {
    if (value) parts.push(`customer[${key}]=${encodeURIComponent(value)}`);
  }

  return parts.length > 0 ? "&" + parts.join("&") : "";
}

/**
 * Builds a full hosted-checkout URL for an item price, with attribution
 * attached.
 */
export function buildCheckoutUrl(itemPriceId: string): string {
  const base = `${CHARGEBEE_CHECKOUT_BASE}?subscription_items[item_price_id][0]=${encodeURIComponent(
    itemPriceId
  )}`;
  return base + getCheckoutAttributionParams();
}
